import jsPDF from "jspdf";
import "jspdf-autotable";
import { getCurrencySymbol, getSortedDataGLOBAL } from "../config/tableHelper";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";

// const handleDownloadPdf = useReactToPrint({
//   contentRef: exportRef,
//   documentTitle: `Ads_Report_${format(new Date(), "yyyy-MM-dd")}`,
//   onBeforeGetContent: async () => {
//     setIsDownloading(true);
//     await new Promise((resolve) => setTimeout(resolve, 100));
//   },
//   onAfterPrint: () => setIsDownloading(false),
//   onPrintError: () => setIsDownloading(false),
// });

// Utility for formatting numbers natively as plain text
const _formatNumber = (val, { fractionDigits = 0 } = {}) => {
  if (val == null || isNaN(Number(val))) return "0";
  return Number(val).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

// const getCurrencySymbol = () => ""; // Set to "$" or "₹" if you want symbols printed

export const getRawTextValue = (col, row) => {
  const value = row[col.key];

  // Helper function to safely extract string value
  const getSafeString = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
      if (val.value !== undefined) return String(val.value);
      if (Array.isArray(val))
        return val
          .map((v) =>
            typeof v === "object"
              ? v.label_name || v.text || JSON.stringify(v)
              : String(v),
          )
          .join(", ");
      return JSON.stringify(val);
    }
    return String(val);
  };

  // Helper to extract numeric value if it's a compare object
  const getSafeNumeric = (val) => {
    if (typeof val === "object" && val !== null && val.value !== undefined)
      return val.value;
    return val;
  };

  // Reporting / Percent differences
  if (
    (col.key.includes("percentage_difference") ||
      row.is_difference ||
      col.type === "percentage-multi") &&
    col.type !== "string"
  ) {
    let formattedValue = _formatNumber(getSafeNumeric(row[col.key]), {
      fractionDigits: 2,
    });
    if (formattedValue === undefined) return "";
    if (typeof formattedValue === "string")
      formattedValue = Number(formattedValue.replace(/[^0-9.-]+/g, ""));
    return formattedValue > 0 ? `+${formattedValue}%` : `${formattedValue}%`;
  }

  switch (col.key) {
    case "status": {
      const st = getSafeString(row.status || "-");
      return st.charAt(0).toUpperCase() + st.slice(1);
    }
    case "headlines": {
      const hl = getSafeString(row[col.key]);
      return hl.replace(/<[^>]*>?/gm, "");
    }
    case "labels": {
      let lbl = row.labels;
      if (
        typeof lbl === "object" &&
        lbl !== null &&
        !Array.isArray(lbl) &&
        lbl.value !== undefined
      ) {
        lbl = lbl.value;
      }
      return Array.isArray(lbl)
        ? lbl.map((l) => l.label_name || l).join(", ")
        : getSafeString(lbl) || "-";
    }
    default:
      // Complex Compare Objects
      if (
        typeof value === "object" &&
        value !== null &&
        value.value !== undefined
      ) {
        // If the column is explicitly a string, we don't want to format it as a number
        if (col.type === "string") {
          const valStr =
            row[col.key + "_current"] !== undefined
              ? row[col.key + "_current"]
              : value.value;
          return String(valStr || "");
        }

        const { value: amount, percent: percentage } = value;
        let displayValue = row[col.key + "_current"];

        if (col.compare_col_type != null) {
          if (col.compare_col_type === "change") displayValue = amount;
          else if (col.compare_col_type === "change_percentage")
            displayValue = percentage;
          else displayValue = row[col.key + col.compare_col_type];
        }

        let formattedDisplayValue;
        const isCurrencyType =
          col.type === "currency" &&
          col.compare_col_type !== "change_percentage";

        if (isCurrencyType) {
          const num = Number(displayValue);
          const isNeg = num < 0;
          formattedDisplayValue = `${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(Math.abs(num), { fractionDigits: 2 })}`;
        } else {
          formattedDisplayValue = _formatNumber(displayValue, {
            fractionDigits: col.type === "number" ? 0 : 2,
          });
          if (
            ["percentage", "percentage-multi", "float"].includes(col.type) ||
            col.compare_col_type === "change_percentage"
          ) {
            formattedDisplayValue += "%";
          }
        }

        if (
          col.compare_col_type == null &&
          percentage !== undefined &&
          percentage !== null
        ) {
          formattedDisplayValue += ` (${percentage > 0 ? "+" : ""}${parseFloat(percentage).toFixed(1)}%)`;
        }
        return formattedDisplayValue;
      }

      // Standard Values
      const returnVal = value ?? "--";
      let cleanVal = 0;
      let parsedNum = 0;

      switch (col.type) {
        case "string":
          return String(returnVal);
        case "number":
          return _formatNumber(returnVal, { fractionDigits: 0 });
        case "float-no-perc":
          cleanVal =
            typeof returnVal === "string"
              ? returnVal.replace(/[^0-9.-]+/g, "")
              : returnVal;
          parsedNum = Number(cleanVal);
          return isNaN(parsedNum) ? "0.00" : parsedNum.toFixed(2);
        case "percentage-multi":
        case "float":
        case "percentage":
          cleanVal =
            typeof returnVal === "string"
              ? returnVal.replace(/[^0-9.-]+/g, "")
              : returnVal;
          parsedNum = Number(cleanVal);
          return isNaN(parsedNum) ? "0.00%" : `${parsedNum.toFixed(2)}%`;
        case "currency":
          const num = Number(returnVal);
          const isNeg = num < 0;
          return `${isNeg ? "-" : ""}${getCurrencySymbol()}${_formatNumber(Math.abs(num), { fractionDigits: 2 })}`;
        default:
          return String(returnVal);
      }
  }
};

const getSafeNumericValue = (row, key, col) => {
  const rawValue = row[key];
  if (
    typeof rawValue === "object" &&
    rawValue !== null &&
    rawValue.value !== undefined
  ) {
    const { value: amount, percent: percentage } = rawValue;
    let displayValue = row[key + "_current"];

    if (col && col.compare_col_type != null) {
      if (col.compare_col_type === "change") displayValue = amount;
      else if (col.compare_col_type === "change_percentage")
        displayValue = percentage;
      else displayValue = row[key + col.compare_col_type];
    }
    return Number(displayValue) || 0;
  }
  const numericValue =
    typeof rawValue === "object" && rawValue !== null
      ? rawValue.value
      : rawValue;
  return Number(numericValue) || 0;
};

export const getRawTotalValue = (col, data) => {
  if (
    !col.calculation &&
    ![
      "number",
      "currency",
      "float",
      "percentage",
      "percentage-multi",
      "float-no-perc",
    ].includes(col.type)
  ) {
    return "";
  }

  const safeData = data ?? [];
  let total = 0;

  if (col.calculation) {
    const { numerator, denominator, multiplier = 1 } = col.calculation;
    const totalNumerator = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, numerator, col),
      0,
    );
    const totalDenominator = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, denominator, col),
      0,
    );

    total =
      totalDenominator > 0
        ? (totalNumerator / totalDenominator) * multiplier
        : 0;

    if (col.compare_col_type === "change_percentage") {
      if (totalDenominator === 0) {
        total = 0;
      } else {
        total =
          ((totalNumerator - totalDenominator) / Math.abs(totalDenominator)) *
          100;
      }
    }
  } else {
    total = safeData.reduce(
      (sum, row) => sum + getSafeNumericValue(row, col.key, col),
      0,
    );
  }

  if (
    (col.key.includes("percentage_difference") ||
      col.type === "percentage-multi") &&
    col.type !== "string"
  ) {
    let formattedValue = _formatNumber(total, { fractionDigits: 2 });
    if (typeof formattedValue === "string")
      formattedValue = Number(formattedValue.replace(/[^0-9.-]+/g, ""));
    return formattedValue > 0 ? `+${formattedValue}%` : `${formattedValue}%`;
  }

  switch (col.type) {
    case "number":
      return _formatNumber(total, { fractionDigits: 0 });
    case "currency":
      const num = Number(total);
      return `${num < 0 ? "-" : ""}${getCurrencySymbol()}${_formatNumber(Math.abs(num), { fractionDigits: 2 })}`;
    case "float-no-perc":
      return `${parseFloat(total).toFixed(2)}`;
    case "percentage-multi":
    case "percentage":
    case "float":
      return `${parseFloat(total).toFixed(2)}%`;
    default:
      return String(total);
  }
};

const loadCustomFonts = async (doc) => {
  try {
    const reqRegular = await fetch(
      "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Regular.ttf",
    );
    const bufRegular = await reqRegular.arrayBuffer();

    const reqBold = await fetch(
      "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Medium.ttf",
    );
    const bufBold = await reqBold.arrayBuffer();

    const toBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++)
        binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };

    doc.addFileToVFS("Roboto-Regular.ttf", toBase64(bufRegular));
    doc.addFileToVFS("Roboto-Medium.ttf", toBase64(bufBold));

    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Medium.ttf", "Roboto", "bold");

    return true;
  } catch (error) {
    console.warn(
      "Could not load custom fonts. Currency symbols may not render properly.",
      error,
    );
    return false;
  }
};

export const generatePDF = async (
  data,
  columns,
  dateRangeTitle = "All time",
  filename = "Campaign_report.pdf",
  titleText = "Campaign report",
) => {
  const doc = new jsPDF("landscape");
  await loadCustomFonts(doc);

  const visibleCols = columns.filter((col) => col.visible !== false);
  if (visibleCols.length === 0) return;

  // --- Apply sorting from SessionStorage before processing rows ---
  let sortedData = [...data];
  try {
    const savedSortStr = sessionStorage.getItem("pdf_sort_config");
    if (savedSortStr) {
      const sortConfig = JSON.parse(savedSortStr);
      if (sortConfig && sortConfig.key) {
        sortedData = getSortedDataGLOBAL(sortConfig, sortedData);
      }
    }
  } catch (err) {
    console.warn("Failed to apply table sorting to PDF", err);
  }

  const MAX_COLS_PER_PAGE = 8;
  const fixedCol = visibleCols[0];
  const dynamicCols = visibleCols.slice(1);

  const chunks = [];
  if (visibleCols.length <= MAX_COLS_PER_PAGE) {
    chunks.push(visibleCols);
  } else {
    const chunkWidth = MAX_COLS_PER_PAGE - 1;
    for (let i = 0; i < dynamicCols.length; i += chunkWidth) {
      chunks.push([fixedCol, ...dynamicCols.slice(i, i + chunkWidth)]);
    }
  }

  // Use the newly sortedData here instead of raw data
  const formattedRows = sortedData.map((row) => {
    const rowObj = {};
    visibleCols.forEach((col) => {
      rowObj[col.key] = getRawTextValue(col, row);
    });
    return rowObj;
  });

  const totalsRow = {};
  visibleCols.forEach((col) => {
    totalsRow[col.key] = getRawTotalValue(col, sortedData);
  });
  totalsRow[fixedCol.key] = "Total";
  formattedRows.push(totalsRow);

  chunks.forEach((chunkCols, index) => {
    if (index > 0) doc.addPage();

    doc.setFont("Roboto", "bold");
    doc.setFontSize(14);

    // 1. Keep only the main title at the top
    doc.text(titleText, 14, 15);

    doc.setFont("Roboto", "normal");
    doc.setFontSize(10);
    doc.text(dateRangeTitle, 14, 22);

    const tableColumns = chunkCols.map((col) => ({
      header: col.title || col.key,
      dataKey: col.key,
    }));

    // 2. Format the part text without parenthesis for the footer
    const partText =
      chunks.length > 1 ? `Part ${index + 1} of ${chunks.length}` : "";

    doc.autoTable({
      columns: tableColumns,
      body: formattedRows,
      startY: 28,
      theme: "grid",
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        font: "Roboto",
        fontStyle: "bold",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        textColor: [0, 0, 0],
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      styles: {
        font: "Roboto",
        fontSize: 8,
        cellPadding: 3,
        valign: "middle",
        overflow: "linebreak",
        minCellWidth: 15,
      },
      willDrawCell: (hookData) => {
        if (hookData.row.index === formattedRows.length - 1) {
          doc.setFont("Roboto", "bold");
        }
      },
      // 3. ADDED didDrawPage HOOK TO PLACE TEXT AT BOTTOM LEFT
      didDrawPage: (hookData) => {
        if (partText) {
          doc.setFont("Roboto", "normal");
          doc.setFontSize(10);

          // Get the height of the page to place it at the bottom dynamically
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height
            ? pageSize.height
            : pageSize.getHeight();

          // Draw text at X:14 (left margin) and Y:pageHeight - 10 (bottom margin)
          doc.text(partText, 14, pageHeight - 10);
        }
      },
    });
  });

  doc.save(filename);
};

// --- Added CSV Export ---
export const generateCSV = async (
  data,
  columns,
  dateRangeTitle = "All time",
  filename = "Campaign_report.csv",
  titleText = "Campaign report",
) => {
  const visibleCols = columns.filter((col) => col.visible !== false);
  if (visibleCols.length === 0) return;

  // Apply sorting from SessionStorage identically to PDF flow
  let sortedData = [...data];
  try {
    const savedSortStr = sessionStorage.getItem("pdf_sort_config");
    if (savedSortStr) {
      const sortConfig = JSON.parse(savedSortStr);
      if (sortConfig && sortConfig.key) {
        sortedData = getSortedDataGLOBAL(sortConfig, sortedData);
      }
    }
  } catch (err) {
    console.warn("Failed to apply table sorting to CSV", err);
  }

  // Safely escape values containing commas, quotes or new lines for standard CSV
  const escapeCSV = (val) => {
    if (val === null || val === undefined) return '""';
    const str = String(val);
    if (
      str.includes('"') ||
      str.includes(",") ||
      str.includes("\n") ||
      str.includes("\r")
    ) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = [];

  // Add the title and date range headers
  rows.push([escapeCSV(titleText)]);
  rows.push([escapeCSV(dateRangeTitle)]);
  rows.push([]); // Empty spacing row

  // Create column headers row
  const colHeaders = visibleCols.map((col) => escapeCSV(col.title || col.key));
  rows.push(colHeaders);

  // Use getRawTextValue for formatting consistently exactly like the PDF
  sortedData.forEach((row) => {
    const rowData = visibleCols.map((col) =>
      escapeCSV(getRawTextValue(col, row)),
    );
    rows.push(rowData);
  });

  // Calculate and generate the Total Row identically to PDF
  const totalsRow = visibleCols.map((col, index) => {
    if (index === 0) return escapeCSV("Total");
    return escapeCSV(getRawTotalValue(col, sortedData));
  });
  rows.push(totalsRow);

  // Form final document string
  const csvContent = rows.map((e) => e.join(",")).join("\n");

  // ADD UTF-8 BOM (\uFEFF) to the start of the string so Excel handles encoding properly
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// --- Added Chart Image/PDF Export ---
export const downloadChart = async (
  chartElement,
  type,
  filename = "Chart_Export",
) => {
  if (!chartElement) {
    console.error("Chart element not found for export");
    return;
  }

  try {
    // 1. Wait 2 seconds to allow any chart animations or layouts to fully stabilize
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // 2. Read dimensions safely WITHOUT modifying the live DOM (which prevents the chart from re-rendering)
    const rect = chartElement.getBoundingClientRect();

    // 3. Capture the element
    const canvas = await html2canvas(chartElement, {
      scale: 2, // Higher scale ensures better quality
      useCORS: true, // Needed if there are external SVGs/images
      backgroundColor: "#ffffff", // White background (prevents black background in PDF)
      width: rect.width,
      height: rect.height,
    });

    const imgData = canvas.toDataURL("image/png");

    if (type === "png") {
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === "pdf") {
      // Setup PDF dimensions based on the canvas aspect ratio
      const pdf = new jsPDF(
        canvas.width > canvas.height ? "landscape" : "portrait",
      );
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${filename}.pdf`);
    }
  } catch (error) {
    console.error("Error generating chart image", error);
  }
};
